import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import { Box, Button, Stack, Typography, CircularProgress, Chip, Badge } from '@mui/material';
import { Close as RemoveIcon } from '@mui/icons-material';
import { QuickSearch } from 'components/_atoms';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import { getDTBPackage } from 'views/App/Portal/DTB/Packages/_data_source/useDTBPackage';
import useDistributableOrganizations, {
  DistributableOrganization,
} from '../_data_sources/useDistributableOrganizations';
import { distributePackage } from '../_data_sources/useManageDistributableDTBPackage';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import { GridCellParams, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { enqueueSnackbar } from 'notistack';
import { neutral } from '_lex/_foundations/phxColors';
import { DTBPackage } from 'views/App/Portal/DTB/_types/dtb';
import { isDtbPackageEligibleForAssignmentOrDistribution } from 'views/App/Portal/DTB/utils/dtbPackageEligibility';
import Loading from 'views/App/Portal/Loading';
import _ from 'lodash';

const DistributePackage: React.FC = () => {
  const { id: dtbPackageId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentOrganizationId } = useContext(AppContext);

  const [dtbPackage, setDTBPackage] = useState<DTBPackage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDistributing, setIsDistributing] = useState(false);
  const [distributableOrganizations, setDistributableOrganizations] = useState<DistributableOrganization[]>([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState<DistributableOrganization[]>([]);
  const [organizationSearch, setOrganizationSearch] = useState<string>('');
  const [showSelectedOrganizations, setShowSelectedOrganizations] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  const organizationsQuery = useDistributableOrganizations(currentOrganizationId || '', dtbPackageId || '');

  // Load package details
  useEffect(() => {
    const fetchPackage = async () => {
      if (!currentOrganizationId || !dtbPackageId) {
        setIsLoading(false);
        return;
      }

      try {
        const packageData = await getDTBPackage(currentOrganizationId, dtbPackageId);
        if (!packageData.dtbs || packageData.dtbs.length === 0) {
          enqueueSnackbar("Can't distribute an empty package", { variant: 'error' });
          navigate('/admin/dtbs/distribution');
          return;
        }
        if (!isDtbPackageEligibleForAssignmentOrDistribution(packageData)) {
          enqueueSnackbar(t('dtbs.distribution.cannotDistributeDraftPackage'), { variant: 'error' });
          navigate('/admin/dtbs/distribution');
          return;
        }
        setDTBPackage(packageData);
      } catch (error) {
        enqueueSnackbar('Failed to load package data.', { variant: 'error' });
        navigate('/admin/dtbs/distribution');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPackage();
  }, [currentOrganizationId, dtbPackageId, navigate]);

  // Load all pages of distributable organizations automatically
  useEffect(() => {
    if (organizationsQuery.data?.pages && organizationsQuery.hasNextPage && !organizationsQuery.isFetchingNextPage) {
      organizationsQuery.fetchNextPage();
    }
  }, [organizationsQuery.data?.pages, organizationsQuery.hasNextPage, organizationsQuery.isFetchingNextPage, organizationsQuery.fetchNextPage]);

  // Load distributable organizations with pagination
  useEffect(() => {
    if (organizationsQuery.data && !organizationsQuery.isError) {
      const fetchedOrgs =
        organizationsQuery.data.pages.flatMap((page: { content: DistributableOrganization[] }) => page.content) || [];
      
      // Deduplicate by ID - keep first occurrence
      const seenIds = new Set<string>();
      const deduplicatedOrgs = fetchedOrgs.filter((org: DistributableOrganization) => {
        if (seenIds.has(org.id)) {
          return false;
        }
        seenIds.add(org.id);
        return true;
      });
      
      setDistributableOrganizations(deduplicatedOrgs);
    }
  }, [organizationsQuery.data, organizationsQuery.isError]);

  // Update selected organizations when row selection changes
  useEffect(() => {
    setSelectedOrganizations(distributableOrganizations.filter((org) => rowSelectionModel.includes(org.id)));
  }, [distributableOrganizations, rowSelectionModel]);

  const handleDistributeClick = async () => {
    if (!currentOrganizationId || !dtbPackageId || selectedOrganizations.length === 0) return;
    if (!dtbPackage || !isDtbPackageEligibleForAssignmentOrDistribution(dtbPackage)) {
      enqueueSnackbar(t('dtbs.distribution.cannotDistributeDraftPackage'), { variant: 'error' });
      return;
    }

    setIsDistributing(true);

    const organizations = selectedOrganizations.map((org) => ({ id: org.id }));

    distributePackage(
      currentOrganizationId,
      dtbPackageId,
      organizations,
      () => {
        setIsDistributing(false);
        enqueueSnackbar('Package distributed successfully!', { variant: 'success' });
        navigate('/admin/dtbs/distribution');
      },
      () => {
        setIsDistributing(false);
        enqueueSnackbar('Failed to distribute package.', { variant: 'error' });
      }
    );
  };

  const organizationColumns: ColumnDef = useMemo(
    () => ({
      name: {
        header: t('dtbs.distribution.selectOrganizations.columns.name', 'Organization Name'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{row.name}</span>;
        },
        valueGetter: (value: any, row: any) => {
          return row.name;
        },
      },
    }),
    [t]
  );

  if (isLoading || !dtbPackage) {
    return <Loading />;
  }

  return (
    <TakeoverLayout
      pageTitle={`${dtbPackage?.name || 'DTB Package'} - Distribution`}
      backUrl='/admin/dtbs/distribution'
      headerProps={{
        title: t('dtbs.distribution.packageDetails.title'),
        subtitle: t('dtbs.distribution.packageDetails.subTitle', '', {
          totalAgencyCount: organizationsQuery.data?.pages?.[0]?.count?.toString() || '',
        }),
      }}
      pageToolbarLeft={
        <QuickSearch
          id='organization_search'
          initialValue={organizationSearch}
          onChange={(val) => setOrganizationSearch(val)}
          placeholder={t('dtbs.distribution.selectOrganizations.search', 'Search organizations')}
        />
      }
      pageToolbarRight={
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant='outlined'
            color='neutral'
            onClick={() => navigate('/admin/dtbs/distribution')}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDistributeClick}
            disabled={isDistributing || selectedOrganizations.length === 0}
            variant='contained'
            endIcon={isDistributing ? <CircularProgress size={16} /> : null}
          >
            {t('dtbs.distribution.distribute', 'Distribute Package')}
          </Button>
        </Box>
      }
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 2, minHeight: 0, height: '100%' }}>
        <Box sx={{ px: 4 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'start',
              gap: 3,
            }}
          >
            {selectedOrganizations.length > 3 && (
              <Button
                className='xs'
                variant='outlined'
                size='small'
                onClick={() => setShowSelectedOrganizations(!showSelectedOrganizations)}
                endIcon={
                  showSelectedOrganizations ? null : (
                    <Badge
                      variant='inline'
                      color='primary'
                      badgeContent={selectedOrganizations.length.toString()}
                    />
                  )
                }
              >
                {showSelectedOrganizations
                  ? t('dtbs.distribution.selectOrganizations.showLess', 'Show Less')
                  : t('dtbs.distribution.selectOrganizations.showAll', 'Show All')}
              </Button>
            )}
            {selectedOrganizations.length > 0 && (
              <>
                <Button
                  className='xs'
                  variant='text'
                  size='small'
                  onClick={() => setRowSelectionModel([])}
                >
                  {t('dtbs.distribution.selectOrganizations.clearAll', 'Clear All')}
                </Button>
                <Box
                  sx={{
                    height: '2rem',
                    border: `solid 1px ${neutral[300]}`,
                  }}
                ></Box>
              </>
            )}
            {_.take(selectedOrganizations, showSelectedOrganizations ? selectedOrganizations.length : 3).map((org) => (
              <Chip
                key={`selected_org_${org.id}`}
                label={org.name}
                onDelete={() => setRowSelectionModel((prev) => prev.filter((x) => x !== org.id))}
                deleteIcon={<RemoveIcon />}
                size='small'
                sx={{
                  justifyContent: 'space-between',
                  gap: 2,
                  height: '2rem',
                  minHeight: '2rem',
                  backgroundColor: neutral[50],
                  '& .MuiChip-label': {
                    lineHeight: '2rem',
                    padding: '0 8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            ))}
          </Box>
        </Box>
        <GridSection
          columns={organizationColumns}
          data={distributableOrganizations}
          query={organizationsQuery}
          totalCount={organizationsQuery.data?.pages?.[0]?.count || distributableOrganizations.length}
          gridProps={{
            getRowId: (row: DistributableOrganization) => row.id,
            checkboxSelection: true,
            onRowSelectionModelChange: (newRowSelectionModel: GridRowSelectionModel) => {
              setRowSelectionModel(newRowSelectionModel);
            },
            rowSelectionModel,
            filterModel: {
              items: [{ field: 'name', operator: 'contains', value: organizationSearch }],
            },
          }}
        />
      </Box>
    </TakeoverLayout>
  );
};

export default DistributePackage;
